/**
 * Pure reducer for the chat stream.
 *
 * The reducer owns the canonical `ChatMessage[]`. Inputs come from:
 *   - the composer (`SEND_USER_NOTE` → optimistic `UserNoteMessage` with
 *     status="sending", later `RESOLVE_USER_NOTE` flips it to `sent` /
 *     `failed`);
 *   - the slash-command flow (`SEND_ASK_QUERY`, `RESOLVE_ASK_QUERY`,
 *     `APPEND_DIGEST`, …);
 *   - the feed poller (`MERGE_FEED` — adds any feed items we don't
 *     already have, dedup by serverId for notes / id for digests).
 *
 * Keeping it pure (no fetches, no side effects) means we can unit-test
 * the entire interaction model with a few `dispatch(...)` calls.
 */
import type { AskMode, DigestPeriod, FeedItem, NoteSourceType } from "../lib/types";
import type {
  AskAnswerMessage,
  AskQueryMessage,
  ChatMessage,
  DigestMessage,
  PendingMessage,
  SystemMessage,
  UserNoteMessage,
} from "./types";

export interface ChatState {
  messages: ChatMessage[];
}

export const initialChatState: ChatState = { messages: [] };

export type ChatAction =
  | {
      type: "SEND_USER_NOTE";
      tempId: string;
      text: string;
      now: string;
    }
  | {
      type: "RESOLVE_USER_NOTE";
      tempId: string;
      serverId: string | null;
      status: "sent" | "failed";
    }
  | {
      type: "SEND_ASK_QUERY";
      id: string;
      question: string;
      now: string;
    }
  | {
      type: "RESOLVE_ASK_QUERY";
      id: string;
      answer:
        | {
            ok: true;
            answerId: string;
            mode: AskMode;
            content: string;
            citedNoteIds: string[];
            now: string;
          }
        | { ok: false };
    }
  | {
      type: "APPEND_DIGEST";
      id: string;
      period: DigestPeriod;
      periodStart: string;
      periodEnd: string;
      content: string;
      createdAt: string;
    }
  | {
      type: "APPEND_SYSTEM";
      id: string;
      text: string;
      variant: "info" | "error";
      now: string;
    }
  | {
      type: "MERGE_FEED";
      items: FeedItem[];
    }
  | {
      type: "ADD_PENDING";
      id: string;
      variant: PendingMessage["variant"];
      now: string;
    }
  | {
      type: "CLEAR_PENDING";
      /** Remove the pending bubble with this id, if any. */
      id: string;
    };

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "SEND_USER_NOTE": {
      const next: UserNoteMessage = {
        kind: "user_note",
        id: action.tempId,
        serverId: null,
        text: action.text,
        status: "sending",
        createdAt: action.now,
      };
      return { messages: [...state.messages, next] };
    }

    case "RESOLVE_USER_NOTE": {
      const messages = state.messages.map((m) => {
        if (m.kind === "user_note" && m.id === action.tempId) {
          return { ...m, serverId: action.serverId, status: action.status };
        }
        return m;
      });
      return { messages };
    }

    case "SEND_ASK_QUERY": {
      const next: AskQueryMessage = {
        kind: "ask_query",
        id: action.id,
        question: action.question,
        status: "asking",
        createdAt: action.now,
      };
      return { messages: [...state.messages, next] };
    }

    case "RESOLVE_ASK_QUERY": {
      const updated = state.messages.map((m) => {
        if (m.kind === "ask_query" && m.id === action.id) {
          return {
            ...m,
            status: action.answer.ok ? ("answered" as const) : ("failed" as const),
          };
        }
        return m;
      });
      if (!action.answer.ok) {
        return { messages: updated };
      }
      const answer: AskAnswerMessage = {
        kind: "ask_answer",
        id: action.answer.answerId,
        questionId: action.id,
        mode: action.answer.mode,
        content: action.answer.content,
        citedNoteIds: action.answer.citedNoteIds,
        createdAt: action.answer.now,
      };
      return { messages: [...updated, answer] };
    }

    case "APPEND_DIGEST": {
      const next: DigestMessage = {
        kind: "digest",
        id: action.id,
        period: action.period,
        periodStart: action.periodStart,
        periodEnd: action.periodEnd,
        content: action.content,
        createdAt: action.createdAt,
      };
      return { messages: [...state.messages, next] };
    }

    case "APPEND_SYSTEM": {
      const next: SystemMessage = {
        kind: "system",
        id: action.id,
        text: action.text,
        variant: action.variant,
        createdAt: action.now,
      };
      return { messages: [...state.messages, next] };
    }

    case "ADD_PENDING": {
      // At most one pending bubble per variant — a second "thinking"
      // while one is already on screen replaces the first rather than
      // stacking two animated dot-rows on top of each other.
      const withoutSameVariant = state.messages.filter(
        (m) => !(m.kind === "pending" && m.variant === action.variant),
      );
      const next: PendingMessage = {
        kind: "pending",
        id: action.id,
        variant: action.variant,
        createdAt: action.now,
      };
      return { messages: [...withoutSameVariant, next] };
    }

    case "CLEAR_PENDING": {
      const filtered = state.messages.filter(
        (m) => !(m.kind === "pending" && m.id === action.id),
      );
      if (filtered.length === state.messages.length) {
        return state;
      }
      return { messages: filtered };
    }

    case "MERGE_FEED": {
      // Build sets of IDs the stream already knows about so we don't
      // double-insert (user-bubble we just confirmed will round-trip as
      // a captured_note from the feed; we keep the user-bubble and
      // skip the duplicate).
      const knownNoteIds = new Set<string>();
      const knownDigestIds = new Set<string>();
      for (const m of state.messages) {
        if (m.kind === "user_note" && m.serverId) {
          knownNoteIds.add(m.serverId);
        } else if (m.kind === "captured_note") {
          knownNoteIds.add(m.id);
        } else if (m.kind === "digest") {
          knownDigestIds.add(m.id);
        }
      }

      const fresh: ChatMessage[] = [];
      for (const item of action.items) {
        if (item.type === "note") {
          if (knownNoteIds.has(item.id)) continue;
          fresh.push(feedNoteToMessage(item.id, item.project_id, item.source_type, item.raw_payload, item.transcript, item.created_at));
        } else {
          if (knownDigestIds.has(item.id)) continue;
          fresh.push({
            kind: "digest",
            id: item.id,
            period: item.period,
            periodStart: item.period_start,
            periodEnd: item.period_end,
            content: item.content,
            createdAt: item.created_at,
          });
        }
      }
      if (fresh.length === 0) {
        return state;
      }
      // A fresh captured_note means the worker round-tripped — drop
      // any outstanding "capturing" pending bubble so the dots don't
      // hang around next to the new captured-note bubble.
      const hasFreshNote = fresh.some((m) => m.kind === "captured_note");
      const base = hasFreshNote
        ? state.messages.filter(
            (m) => !(m.kind === "pending" && m.variant === "capturing"),
          )
        : state.messages;
      // Merge + re-sort by createdAt ascending so the latest is at the
      // bottom of the stream.
      const merged = [...base, ...fresh].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      );
      return { messages: merged };
    }

    default: {
      // Exhaustiveness check — TypeScript will flag any missing case.
      const _exhaustive: never = action;
      void _exhaustive;
      return state;
    }
  }
}

function feedNoteToMessage(
  id: string,
  projectId: string | null,
  sourceType: NoteSourceType,
  rawPayload: string,
  transcript: string | null,
  createdAt: string,
): ChatMessage {
  return {
    kind: "captured_note",
    id,
    projectId,
    sourceType,
    rawPayload,
    transcript,
    createdAt,
  };
}
